// js/app-export-trail.js — the export trail (EXPORT_HISTORY).
//
// Split out of js/app-core.js (September 2026) when the failure-recording work
// for EXP-06 pushed that file past its FROZEN 1,700-line ceiling. The rule is
// split, never raise, so this is the slice that came out: everything that
// writes, reads or clears STORAGE_KEYS.EXPORT_HISTORY.
//
// It is the E&O paper trail -- "what did we send this client, and when" -- so
// it is encrypted at rest (SecureStorage SENSITIVE) and cloud-synced
// (DOC_LOCAL_KEYS.exportHistory, union-merged as an append-only ring with cap
// 500 by js/sync-merge.js). The cap here and the cap there must agree.
//
// Must load EAGERLY, after app-core.js: js/boat-quote-export.js,
// js/accounting-export.js, js/hawksoft-export.js, js/app-property-report-pdf.js,
// js/call-hub-panels.js and the personal exporters all call App.logExport, and
// several of those are reachable without the quoting tool ever being opened.
// Every one of them feature-detects it at CALL time -- including the five
// personal exporters in this same package (app-export.js, -pdf, -csv,
// -cmsmtf, -acord-xml), which called it bare until a review pointed out
// what the split had changed: while this lived in app-core.js a failure to
// load meant nothing worked anyway, but a separate file is a new
// single-file failure mode, and in all five the download happens FIRST --
// so a throw here puts the file in the user's Downloads folder while
// _runExportFn's catch tells them it failed. Exactly the false 'export
// failed' this module's own docblock says it removed for a locked vault.
// Same rule as js/app-recovery-bucket.js: a slice that fails to load
// degrades to 'no trail line', never to a thrown export.
'use strict';

Object.assign(App, {
    /**
     * The ONE writer for the export trail. logExport, logExportFailure and
     * logExportFor all funnel through it, so a field added to one cannot be
     * missing from another.
     *
     * Returns false rather than throwing when the write is refused. That
     * matters because two of its three callers run from inside an exporter's
     * failure handler: a throw there would replace the agent's real error with
     * a crash. It also fixes a quieter case that predates this -- EXPORT_HISTORY
     * is in SecureStorage's SENSITIVE list, and a write to a sensitive key
     * THROWS while the v2 vault is locked, so a locked-vault export used to
     * download the file and then report "export failed" from the exporter's own
     * catch.
     */
    _writeExportTrail(entry) {
        const key = STORAGE_KEYS.EXPORT_HISTORY;
        let history = Utils.tryParseLS(key, []);
        history.unshift(entry);
        // 200 was low enough that a busy month evicted the start of the year.
        history = history.slice(0, 500);
        try {
            localStorage.setItem(key, JSON.stringify(history));
        } catch (err) {
            if (err && err.code === 'SECURE_STORAGE_LOCKED') return false;
            console.warn('[App] export trail write failed:', err);
            return false;
        }
        // Push to the cloud. Without this the export trail was device-local:
        // it lived on one browser, was never backed up, and vanished with the
        // profile, which is precisely what a paper trail must not do.
        if (window.Sync && typeof Sync.schedulePush === 'function') {
            try { Sync.schedulePush(); } catch (_) { /* non-fatal */ }
        }
        // Also save to disk (fire-and-forget) — localhost dev only; production has
        // no /local/ endpoint. `typeof fetch` guarded: this now runs from inside
        // exporter failure handlers, and an environment without fetch must not
        // turn the audit write into a second, louder error.
        if (typeof fetch === 'function' && (location.hostname === 'localhost' || location.hostname === '127.0.0.1')) {
            fetch('/local/export-history', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(entry)
            }).catch(() => {});
        }
        if (typeof this.renderReviewPanelPreviews === 'function') this.renderReviewPanelPreviews();
        return true;
    },

    /**
     * Build a trail entry. `identity` lets a caller that is NOT the personal
     * intake say who the export was for; without it the entry is stamped from
     * App.data, which is right for every personal-lines exporter and wrong for
     * every other quoter.
     *
     * Successes deliberately carry NO `ok` key. Absent reads as success -- that
     * is what js/insurance-agent-tools.js already assumes -- so recording
     * failures does not mean rewriting up to 500 existing cloud entries.
     *
     * `identity.exportedAt` overrides the clock, and exists for one shape: a
     * report PULLED BACK from somewhere else, where the export happened at a
     * moment this tab was not present for. Stamping `now` there both lies about
     * when the file went out and defeats the union merge -- the standoff
     * identity is `exportedAt|type|filename|clientName`, so two pulls of the
     * SAME fill would differ only in the field the merge keys on and both would
     * survive across devices. Callers with a real clock must not pass it.
     */
    _exportTrailEntry(type, filename, identity) {
        const id = identity || {};
        const fromIntake = `${this.data.firstName || ''} ${this.data.lastName || ''}`.trim();
        return {
            type,
            filename,
            clientName: id.clientName || fromIntake || 'Unknown',
            // Ties the export to a specific record instead of a name string, so
            // "what did we send this client" is answerable even when two
            // clients share a name.
            _clientId: Object.prototype.hasOwnProperty.call(id, 'clientId')
                ? (id.clientId || null)
                : (this.activeClientId || (this.data && this.data._clientId) || null),
            exportedAt: id.exportedAt || new Date().toISOString(),
            qType: id.qType || (document.querySelector('input[name="qType"]:checked') || {}).value || 'unknown',
        };
    },

    /**
     * Mirror to the activity log. NO client name and NO name-bearing filename
     * here (2026-09 NPI audit, F3): ACTIVITY_LOG is plaintext at rest by design
     * ("non-PII operational state"), so the entry carries the export type only
     * — the full detail lives in EXPORT_HISTORY, which is encrypted and synced.
     */
    _mirrorExportToActivity(type, ok, error) {
        if (!window.ActivityLog) return;
        window.ActivityLog.add({
            type: ok ? 'export' : 'error',
            area: String(type || '').toLowerCase(),
            ok: !!ok,
            message: ok ? `${type} exported` : `${type} export failed`,
            detail: ok ? undefined : String(error || '').slice(0, 200),
        });
    },

    /**
     * The personal intake's logger, and the only one with a SIDE EFFECT:
     * _saveClientHistoryNow() files the open quote into CLIENT_HISTORY, on the
     * reasoning that a client you exported for is a client worth keeping.
     *
     * That makes it the wrong logger wherever there may be no client record to
     * file. It INVENTS one: js/hawksoft-export.js's create-in-HawkSoft button
     * logged through here and a walk-in with no record open came back holding a
     * client-history row, breaking CID-02's contract that the path writes
     * nothing locally. Such a caller wants logExportFor(type, filename, {}) --
     * an empty identity leaves _exportTrailEntry on these same fallbacks, so
     * the trail line is byte-identical and only the side effect is gone.
     */
    logExport(type, filename) {
        try { this._saveClientHistoryNow(); } catch(e) { /* ok */ }
        this._writeExportTrail(this._exportTrailEntry(type, filename));
        this._mirrorExportToActivity(type, true);
    },

    /**
     * An export that did NOT happen. The trail recorded successes only, so the
     * one question it exists to answer -- "did this client ever get the file"
     * -- read the same for a file that went out and a file that died in the
     * exporter. `ok: false` and a bounded reason close that.
     *
     * Deliberately does NOT call _saveClientHistoryNow: nothing was produced,
     * so there is no reason to move the client record on.
     */
    logExportFailure(type, filename, error) {
        const entry = this._exportTrailEntry(type, filename);
        entry.ok = false;
        // Bounded: this is a provider or browser string and it is synced.
        entry.error = String((error && error.message) || error || 'Unknown error').slice(0, 200);
        this._writeExportTrail(entry);
        this._mirrorExportToActivity(type, false, entry.error);
    },

    /**
     * For a quoter that is NOT the personal intake. logExport reads
     * this.data.firstName / this.activeClientId, so a commercial or boat export
     * logged through it is filed under whatever personal client happens to be
     * loaded -- "Acme Roofing" filed under "Jane Sample", or "Unknown" -- and
     * _saveClientHistoryNow writes a spurious personal client-history row on
     * top. Pass the identity instead.
     */
    /**
     * The commercial quoter's door onto logExportFor. It lives here rather than
     * in js/commercial-quoter.js because that file sits against its 1,800-line
     * budget, and because WHICH logger a foreign quoter must use is knowledge
     * about the trail, not about the quoter.
     */
    logCommercialExport(type, filename, info) {
        const d = info || {};
        this.logExportFor(type, filename || '', {
            clientName: String(d.bizName || '').trim() || 'Unknown',
            clientId: d.quoteId || null,
            qType: 'commercial',
        });
    },

    /**
     * The HawkSoft tool's door onto logExportFor, and it needs one for a reason
     * the personal exporters do not have: that tool has its OWN loaded client.
     * _loadFromHistory puts a different CLIENT_HISTORY record into _exportData
     * and never touches App.data, so logExport would file a CMSMTF built for
     * client B -- or a client CREATED as B over the v4 API -- under whoever
     * happens to be open in the intake, and run _saveClientHistoryNow() on them
     * as well. clientId is always an OWN key here, including when it is null
     * (a walk-in with no record), which is what keeps _exportTrailEntry off its
     * activeClientId fallback rather than merely hoping it is empty.
     *
     * It takes the tool's raw state -- {client, clientId, commercial} -- rather
     * than a finished identity so the derivation lives here, in the module that
     * owns the trail's shape, and so js/hawksoft-export.js pays one line per
     * call site against its 1,800-line budget instead of a local builder.
     */
    logHawkSoftExport(type, filename, info) {
        const d = info || {};
        const c = d.client || {};
        const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
        this.logExportFor(type, filename || '', {
            clientName: name || c.businessName || 'Unknown',
            clientId: d.clientId || null,
            qType: d.commercial ? 'commercial' : ((this.data && this.data.qType) || 'unknown'),
        });
    },

    logExportFor(type, filename, identity) {
        const entry = this._exportTrailEntry(type, filename, identity || {});
        if (identity && identity.ok === false) {
            entry.ok = false;
            entry.error = String((identity.error && identity.error.message) || identity.error || 'Unknown error').slice(0, 200);
        }
        // The write's own answer: false when it was refused (a locked vault),
        // so a caller that must not mark the entry as filed can tell.
        const written = this._writeExportTrail(entry);
        this._mirrorExportToActivity(type, !(identity && identity.ok === false), identity && identity.error);
        return written;
    },

    async loadExportHistory() {
        const container = document.getElementById('exportHistoryList');
        if (!container) return;
        // Try disk first (localhost dev only), fall back to localStorage
        let entries = [];
        if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
            try {
                const res = await fetch('/local/export-history');
                if (res.ok) {
                    const data = await res.json();
                    entries = data.entries || [];
                }
            } catch (e) {}
        }
        if (entries.length === 0) {
            entries = Utils.tryParseLS(STORAGE_KEYS.EXPORT_HISTORY, []);
        }
        if (entries.length === 0) {
            container.innerHTML = '<p style="color:var(--text-secondary);font-style:italic;">No exports yet. Export a file to see it here.</p>';
            return;
        }
        const typeLabels = { CMSMTF: '📂 HawkSoft', XML: '🚗 Auto XML', HomeXML: '🏠 Home XML', BothXML: '📦 Both XML', PDF: '📄 PDF', Text: '📝 Text', CSV: '📊 CSV', 'HawkSoft Client': '🦅 HawkSoft client', 'EZLynx Fill': '⚡ EZLynx fill' };
        container.innerHTML = `
            <div style="max-height:300px;overflow-y:auto;">
                <table style="width:100%;border-collapse:collapse;font-size:13px;">
                    <thead><tr style="text-align:left;border-bottom:1px solid var(--border);">
                        <th style="padding:6px 8px;">Type</th>
                        <th style="padding:6px 8px;">Client</th>
                        <th style="padding:6px 8px;">File</th>
                        <th style="padding:6px 8px;">Date</th>
                    </tr></thead>
                    <tbody>
                        ${entries.slice(0, 50).map(e => {
                            const d = new Date(e.exportedAt);
                            const dateStr = d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'});
                            const esc = (s) => Utils.escapeHTML(s);
                            // escapeATTR for the title, not escapeHTML: the
                            // latter is textContent -> innerHTML, which leaves
                            // `"` alone, and e.error is a provider or browser
                            // string that arrives over sync from another device.
                            const escA = (s) => Utils.escapeAttr(s);
                            // A row that reads `ok: false` is an export that did
                            // NOT happen. This table is the E&O paper trail, so
                            // painting it as an ordinary row would be worse than
                            // the pre-failure-recording behaviour, where a dead
                            // export simply wrote nothing: it would assert a file
                            // went out. Absent still reads as success (see
                            // _exportTrailEntry) so no existing row moves.
                            const failed = e.ok === false;
                            const label = Object.prototype.hasOwnProperty.call(typeLabels, e.type)
                                ? typeLabels[e.type] : esc(e.type || '');
                            return `<tr style="border-bottom:1px solid color-mix(in srgb, var(--border) 50%, transparent);${failed ? 'opacity:.75;' : ''}">
                                <td style="padding:6px 8px;white-space:nowrap;" title="${escA(failed ? ('Failed: ' + (e.error || 'Unknown error')) : '')}">${failed ? '<span style="color:var(--apple-red,#ff3b30);font-weight:600;">✕</span> ' : ''}${label}</td>
                                <td style="padding:6px 8px;font-weight:500;">${esc(e.clientName || '')}</td>
                                <td style="padding:6px 8px;font-family:monospace;font-size:12px;max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${esc(e.filename || '')}</td>
                                <td style="padding:6px 8px;white-space:nowrap;color:var(--text-secondary);">${dateStr}</td>
                            </tr>`;
                        }).join('')}
                    </tbody>
                </table>
            </div>
            <div style="margin-top:8px;text-align:right;">
                <button class="btn btn-tertiary" onclick="App.clearExportHistory()">Clear History</button>
            </div>
        `;
    },

    clearExportHistory() {
        if (!confirm('Clear export history?')) return;
        localStorage.removeItem(STORAGE_KEYS.EXPORT_HISTORY);

        // A-438: removing the local key is NOT enough. `exportHistory` is in
        // DOC_LOCAL_KEYS, and _pushAllBlobs SKIPS any doc whose local key is
        // absent — so the cloud row survived and the next restoreFromCloud()
        // wrote the whole history back. Worse here than for a draft: it is also
        // in DOC_STANDOFF_MERGES, so the union merge would resurrect it even
        // without a clean restore. The agent believes the E&O export trail was
        // cleared and it silently returns.
        //
        // Same shape as BoatQuote.clearDraft: DELETE the row, never
        // schedulePush(). Migration 0021's archive trigger keeps the deleted row
        // in user_blob_history, so an operator can still recover it.
        try {
            if (window.Sync && Sync.deleteBlob) {
                // Not awaited — the UI must not block on the network — but the
                // outcome IS checked. The facade silently skips every write
                // while sync is MFA- or policy-blocked, and a silent skip here
                // would leave the agent believing a destructive action landed.
                Promise.resolve(Sync.deleteBlob('exportHistory'))
                    .then((res) => {
                        if (res && res.ok) return;
                        const why = (res && res.skipped) ? String(res.skipped) : 'the sync service refused it';
                        console.warn('[App] cloud export-history delete did not complete:', why);
                        if (this.toast) {
                            this.toast('Cleared on this device, but not in the cloud. It may reappear after a sync.',
                                { type: 'error', duration: 5000 });
                        }
                    })
                    .catch((e) => { console.warn('[App] cloud export-history delete failed:', e); });
            } else if (window.Sync && Sync.schedulePush) {
                Sync.schedulePush();
            }
        } catch (e) { /* a failed cloud delete must not block the local clear */ }

        if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
            fetch('/local/export-history', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) }).catch(() => {});
        }
        this.loadExportHistory();
        if (typeof this.renderReviewPanelPreviews === 'function') this.renderReviewPanelPreviews();
    },
});
